import { useState, useEffect } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Film, Users, Settings, LogOut, BarChart3, Trophy, ChevronRight, ChevronDown, Menu, X, UserCog, UserCircle, Map, ClipboardList, Lock, Calendar, Home } from 'lucide-react'
import Logo from './Logo'
import { useAuth } from '../context/AuthContext'
import { estMembreDeClub, estVraiFree } from '../utils/planAccess'
import { useSeason, formatSeasonShort } from '../context/SeasonContext'
import TrialBanner from '../components/TrialBanner'
import ClubLogoOrName from '../components/ClubLogoOrName'
import { T, globalStyles } from '../theme'

const W = T.sidebarW

const API = import.meta.env.VITE_API_URL || 'https://backend-pued.onrender.com/api'

function authHeaders() {
  const token = localStorage.getItem('insightball_token')
  return { Authorization: `Bearer ${token}` }
}

function DashboardLayout({ children }) {
  const location = useLocation()
  const navigate  = useNavigate()
  const { user, logout } = useAuth()
  const { selectedSeason, setSelectedSeason, availableSeasons, setAvailableSeasons, currentSeason, nextSeason, isCurrentSeason, isNextSeason, isPastSeason, isSeasonPrepared, isOrphan, orphanClubName } = useSeason()
  const [mobileOpen, setMobileOpen]     = useState(false)
  const [isMobile,   setIsMobile]       = useState(() => typeof window !== 'undefined' && window.innerWidth < 768)
  const [pendingCount, setPendingCount] = useState(0)
  const [seasonOpen, setSeasonOpen]     = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [])

  useEffect(() => { setMobileOpen(false) }, [location.pathname])

  // Fermer dropdown saison au clic extérieur
  // On utilise un data-attribute plutôt qu'un ref car SidebarContent est rendu deux fois
  // (desktop + mobile drawer) et React ne garde qu'un seul ref valide.
  useEffect(() => {
    if (!seasonOpen) return
    const handleClick = (e) => {
      if (!e.target.closest('[data-season-selector]')) {
        setSeasonOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [seasonOpen])

  // Charger la liste des saisons disponibles
  // NB : on ne met PAS setAvailableSeasons dans les deps. Cette fonction vient
  // de SeasonContext et sa référence change à chaque render du provider,
  // ce qui causait une boucle infinie de fetchs sur /matches/seasons/.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!user) return
    fetch(`${API}/matches/seasons/`, { headers: authHeaders() })
      .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.json() })
      .then(data => {
        if (data?.seasons && Array.isArray(data.seasons)) {
          setAvailableSeasons(data.seasons)
        }
      })
      .catch(() => {})
  }, [user])

  // Badge "à valider" désactivé tant que le pipeline vidéo n'est pas en prod.
  // Le backend ne filtre pas encore par validation_status, donc le compteur
  // remontait le nombre total de matchs du user, pas ceux en validation.
  // À réactiver quand le pipeline Lucas livrera des matchs en pending_validation.
  // useEffect(() => {
  //   if (!user) return
  //   const fetchPending = () => {
  //     fetch(`${API}/matches/?validation_status=pending_validation`, { headers: authHeaders() })
  //       .then(r => { if (!r.ok) throw new Error(String(r.status)); return r.json() })
  //       .then(data => {
  //         const count = Array.isArray(data) ? data.length : (data?.items?.length ?? 0)
  //         setPendingCount(count)
  //       })
  //       .catch(() => setPendingCount(0))
  //   }
  //   fetchPending()
  //   const iv = setInterval(fetchPending, 60000)
  //   return () => clearInterval(iv)
  // }, [user])

  const isClubMember = estMembreDeClub(user)
  const isClubPlan   = user?.plan === 'CLUB' || user?.plan === 'CLUB_PRO'
  const isClubAdmin  = isClubPlan && (user?.role === 'ADMIN' || user?.role === 'admin')
  const isClubUser   = isClubAdmin || isClubMember

  // Plan FREE : fonctionnalités avancées verrouillées.
  // A7 · un membre de club portait ce bandeau d'upsell en permanence dans sa
  // barre latérale, alors que son club est déjà abonné.
  const isFree  = estVraiFree(user)

  const navigation = isClubAdmin ? [
    { name: 'Accueil',      href: '/dashboard',              icon: Home },
    { name: 'Matchs',       href: '/dashboard/matches',      icon: Film, badge: pendingCount },
    { name: 'Effectif',     href: '/dashboard/players',      icon: Users },
    { name: 'Statistiques', href: '/dashboard/stats',        icon: BarChart3 },
    { name: 'Membres',      href: '/dashboard/members',      icon: UserCog },
    { name: 'Séances',      href: '/dashboard/seances',      icon: ClipboardList,
      activePrefixes: ['/dashboard/seances', '/dashboard/exercises'] },
    { name: 'Insightboard', href: '/dashboard/insightboard', icon: Map },
    { name: 'Mon profil',   href: '/dashboard/mon-profil',   icon: UserCircle },
    { name: 'Paramètres',   href: '/dashboard/settings',     icon: Settings },
  ] : [
    { name: 'Accueil',      href: '/dashboard',              icon: Home },
    { name: 'Matchs',       href: '/dashboard/matches',      icon: Film, badge: pendingCount },
    { name: 'Effectif',     href: '/dashboard/players',      icon: Users },
    { name: 'Statistiques', href: '/dashboard/stats',        icon: BarChart3 },
    { name: 'Séances',      href: '/dashboard/seances',      icon: ClipboardList,
      activePrefixes: ['/dashboard/seances', '/dashboard/exercises'] },
    { name: 'Insightboard', href: '/dashboard/insightboard', icon: Map },
    { name: 'Mon profil',   href: '/dashboard/mon-profil',   icon: UserCircle },
    { name: 'Paramètres',   href: '/dashboard/settings',     icon: Settings },
  ]

  const handleLogout = () => { logout(); navigate('/login') }

  const initials = user?.name
    ? user.name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : '?'

  // Active state : match exact pour /dashboard, sinon startsWith.
  // Un item peut declarer `activePrefixes` (array) pour rester actif sur
  // d'autres branches d'URL (ex: Catalogue actif sur /dashboard/exercises/*).
  const isActive = (item) => {
    const href = typeof item === 'string' ? item : item.href
    const prefixes = typeof item === 'object' && item.activePrefixes
      ? item.activePrefixes
      : [href]
    if (href === '/dashboard') return location.pathname === href
    return prefixes.some(p => location.pathname.startsWith(p))
  }

  const getPlanBadge = () => {
    if (isClubMember) return 'CLUB'
    const plan = user?.plan || 'FREE'
    // Affichage lisible des plans
    const labels = { FREE: 'FREE', COACH: 'COACH+', CLUB: 'CLUB', CLUB_PRO: 'CLUB PRO' }
    return labels[plan] || plan
  }

  const SidebarContent = () => (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.sidebarBg, borderRight: `1px solid ${T.sidebarBorder}` }}>

      {/* Logo */}
      <div style={{
        padding: '18px 16px',
        borderBottom: `1px solid ${T.sidebarBorder}`,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <Logo variant="full" theme="light" size={28} />
        </Link>
        {isMobile && (
          <button onClick={() => setMobileOpen(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.muted, padding: 4 }}>
            <X size={16} />
          </button>
        )}
      </div>

      {/* User card */}
      <div style={{ padding: '12px 14px', borderBottom: `1px solid ${T.sidebarBorder}` }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <div style={{
            width: 32, height: 32, flexShrink: 0, borderRadius: '50%',
            background: T.rule, border: `1px solid ${T.ruleDark}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontFamily: T.display, fontSize: 12, fontWeight: 700, color: T.ink2 }}>{initials}</span>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ fontFamily: T.mono, fontSize: 12, color: T.ink, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {user?.name}
            </p>
            <p style={{ fontFamily: T.mono, fontSize: 10, color: T.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 1 }}>
              {user?.email}
            </p>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {user?.plan && (
            <span style={{
              fontFamily: T.mono, fontSize: 8, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase',
              padding: '3px 8px', background: T.goldBg, border: `1px solid ${T.goldBdr}`, color: T.gold,
              borderRadius: T.radius,
            }}>
              {getPlanBadge()}
            </span>
          )}
          {isClubUser && user?.role && !isClubMember && !['COACH', 'coach'].includes(user.role) && (
            <span style={{
              fontFamily: T.mono, fontSize: 8, fontWeight: 500, letterSpacing: '.08em', textTransform: 'uppercase',
              padding: '3px 8px', background: T.bgAlt, border: `1px solid ${T.rule}`, color: T.muted,
              borderRadius: T.radius,
            }}>
              {user.role}
            </span>
          )}
        </div>

        {/* Bandeau upgrade FREE */}
        {isFree && (
          <Link to="/tarifs" style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            marginTop: 10, padding: '8px 10px',
            background: T.dark, borderRadius: T.radius,
            textDecoration: 'none', transition: 'opacity .12s', minHeight: 36,
          }}
            onMouseEnter={e => e.currentTarget.style.opacity = '0.85'}
            onMouseLeave={e => e.currentTarget.style.opacity = '1'}
          >
            <span style={{ fontFamily: T.mono, fontSize: 9, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: '#FFFFFF' }}>Passer en Coach+</span>
            <ChevronRight size={10} color="#FFFFFF" />
          </Link>
        )}
      </div>

      {/* Sélecteur de saison */}
      <div data-season-selector style={{ padding: '10px 14px', borderBottom: `1px solid ${T.sidebarBorder}`, position: 'relative' }}>
        <p style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 600, letterSpacing: '.15em', textTransform: 'uppercase', color: T.sidebarSection, marginBottom: 6 }}>
          Saison
        </p>
        <button
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => { e.stopPropagation(); setSeasonOpen(o => !o) }}
          style={{
            width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            gap: 8, padding: '7px 10px',
            background: seasonOpen ? T.rule : T.bgAlt,
            border: `1px solid ${seasonOpen ? T.ruleDark : T.rule}`,
            borderRadius: T.radius,
            cursor: 'pointer', transition: 'all .12s',
          }}
          onMouseEnter={e => { if (!seasonOpen) e.currentTarget.style.borderColor = T.ruleDark }}
          onMouseLeave={e => { if (!seasonOpen) e.currentTarget.style.borderColor = T.rule }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Calendar size={11} color={isCurrentSeason ? T.gold : T.muted} />
            <span style={{
              fontFamily: T.mono, fontSize: 11, fontWeight: 600,
              color: T.ink,
            }}>
              {selectedSeason}
            </span>
            {isPastSeason ? (
              <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: T.muted2 }}>
                terminée
              </span>
            ) : isCurrentSeason ? (
              <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: T.green }}>
                en cours
              </span>
            ) : !isSeasonPrepared(selectedSeason) ? (
              <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: T.gold, padding: '1px 5px', border: `1px solid ${T.goldBdr}`, borderRadius: 20 }}>
                à préparer
              </span>
            ) : (
              <span style={{ fontFamily: T.mono, fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: T.blue }}>
                à venir
              </span>
            )}
          </div>
          <ChevronDown size={10} color={T.muted} style={{ transform: seasonOpen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
        </button>

        {/* Dropdown saisons */}
        {seasonOpen && (
          <div style={{
            position: 'absolute', top: 'calc(100% - 4px)', left: 14, right: 14,
            background: T.surface, border: `1px solid ${T.rule}`,
            borderRadius: T.radius,
            zIndex: 50, maxHeight: 240, overflowY: 'auto',
            boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
          }}>
            {availableSeasons.filter(s => s >= currentSeason).map(season => {
              const active = season === selectedSeason
              const isCurrent = season === currentSeason
              const isPrepared = isSeasonPrepared(season)
              const isPast = season < currentSeason
              return (
                <button
                  key={season}
                  onMouseDown={(e) => e.stopPropagation()}
                  onClick={(e) => {
                    e.stopPropagation()
                    setSelectedSeason(season)
                    setSeasonOpen(false)
                    setTimeout(() => window.location.reload(), 50)
                  }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '9px 12px',
                    background: active ? T.rule : 'transparent',
                    borderLeft: 'none', border: 'none', borderBottom: `1px solid ${T.rule}`,
                    cursor: 'pointer', textAlign: 'left',
                    fontFamily: T.mono, fontSize: 11, fontWeight: active ? 600 : 400,
                    color: active ? T.ink : T.ink2,
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = T.bgAlt }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent' }}
                >
                  <span>{season}</span>
                  {isPast ? (
                    <span style={{ fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: T.muted2 }}>
                      terminée
                    </span>
                  ) : isCurrent ? (
                    <span style={{ fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: T.green }}>
                      en cours
                    </span>
                  ) : !isPrepared ? (
                    <span style={{ fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: T.gold, padding: '1px 5px', border: `1px solid ${T.goldBdr}`, borderRadius: 20 }}>
                      à préparer
                    </span>
                  ) : (
                    <span style={{ fontSize: 8, letterSpacing: '.1em', textTransform: 'uppercase', color: T.blue }}>
                      à venir
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>

      {/* Nav */}
      <nav style={{ padding: '8px 8px', flex: 1, overflowY: 'auto' }}>
        <p style={{ fontFamily: T.mono, fontSize: 8, fontWeight: 600, letterSpacing: '.15em', textTransform: 'uppercase', color: T.sidebarSection, padding: '10px 8px 6px' }}>
          Navigation
        </p>
        {navigation.map(item => {
          const active   = isActive(item)
          const hasBadge = item.badge > 0
          const locked   = !!item.locked
          return (
            <Link key={item.name} to={item.href} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '9px 10px', marginBottom: 2,
              background: active ? T.sidebarActiveB : 'transparent',
              borderRadius: T.radius,
              color: locked
                ? T.muted2
                : active ? T.sidebarActiveColor : T.sidebarColor,
              fontFamily: T.mono, fontSize: 12, fontWeight: active ? 600 : 500,
              letterSpacing: '.02em',
              textDecoration: 'none', transition: 'all .1s',
              opacity: locked ? 0.5 : 1,
              minHeight: 36,
            }}
              onMouseEnter={e => { if (!active && !locked) { e.currentTarget.style.background = T.bgAlt; e.currentTarget.style.color = T.ink } }}
              onMouseLeave={e => { if (!active && !locked) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = T.sidebarColor } }}
            >
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <item.icon size={14} strokeWidth={active ? 2 : 1.5} />
                {hasBadge && (
                  <span style={{
                    position: 'absolute', top: -4, right: -5,
                    width: 7, height: 7, borderRadius: '50%',
                    background: T.red,
                    border: `1.5px solid ${T.surface}`,
                  }} />
                )}
              </div>
              <span style={{ flex: 1 }}>{item.name}</span>
              {locked && <Lock size={9} color={T.muted2} />}
              {hasBadge && !locked && (
                <span style={{
                  fontFamily: T.mono, fontSize: 8, fontWeight: 600, letterSpacing: '.04em',
                  padding: '2px 6px',
                  background: T.redBg,
                  color: T.red,
                  border: `1px solid ${T.redBdr}`,
                  borderRadius: 20,
                }}>
                  {item.badge}
                </span>
              )}
            </Link>
          )
        })}
      </nav>

      {/* Footer sidebar */}
      <div style={{ borderTop: `1px solid ${T.sidebarBorder}`, padding: '8px 8px' }}>
        {user?.club_name && (
          <div style={{ padding: '6px 8px 8px', display: 'flex', alignItems: 'center', gap: 8, background: T.bgAlt, borderRadius: T.radius, marginBottom: 4 }}>
            <ClubLogoOrName size="sm" name={user.club_name} />
          </div>
        )}
        <button onClick={handleLogout} style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 10,
          padding: '9px 10px', background: 'transparent', border: 'none',
          color: T.muted, fontFamily: T.mono, fontSize: 11, fontWeight: 500,
          cursor: 'pointer', transition: 'all .12s', borderRadius: T.radius,
          minHeight: 36,
        }}
          onMouseEnter={e => { e.currentTarget.style.color = T.red; e.currentTarget.style.background = T.redBg }}
          onMouseLeave={e => { e.currentTarget.style.color = T.muted; e.currentTarget.style.background = 'transparent' }}
        >
          <LogOut size={13} strokeWidth={1.5} />
          Déconnexion
        </button>
      </div>
    </div>
  )

  return (
    <div style={{ minHeight: '100vh', background: T.bg, display: 'flex', overflowX: 'hidden', width: '100%', fontFamily: T.mono }}>
      <style>{globalStyles}</style>

      {/* ===== Desktop sidebar ===== */}
      <aside data-print="hide" style={{
        position: 'fixed', top: 0, left: 0, bottom: 0,
        width: W, zIndex: 50, overflowY: 'auto',
        display: isMobile ? 'none' : 'block',
      }}>
        <SidebarContent />
      </aside>

      {/* ===== Mobile drawer backdrop ===== */}
      <div
        data-print="hide"
        onClick={() => setMobileOpen(false)}
        style={{
          position: 'fixed', inset: 0,
          background: 'rgba(10,9,8,0.6)',
          zIndex: 299,
          opacity: isMobile && mobileOpen ? 1 : 0,
          pointerEvents: isMobile && mobileOpen ? 'auto' : 'none',
          transition: 'opacity .22s cubic-bezier(.4,0,.2,1)',
        }}
      />
      <aside data-print="hide" style={{
        position: 'fixed', top: 0, left: 0, bottom: 0, width: 260,
        zIndex: 300,
        transform: isMobile && mobileOpen ? 'translateX(0)' : 'translateX(-100%)',
        transition: 'transform .22s cubic-bezier(.4,0,.2,1)',
        overflowY: 'auto',
        visibility: isMobile ? 'visible' : 'hidden',
      }}>
        <SidebarContent />
      </aside>

      {/* ===== Mobile top bar ===== */}
      <header data-print="hide" style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: 52,
        background: T.topbar || T.surface, borderBottom: `1px solid ${T.sidebarBorder}`,
        display: isMobile ? 'flex' : 'none',
        alignItems: 'center', justifyContent: 'space-between',
        padding: '0 16px', zIndex: 80,
      }}>
        <button onClick={() => setMobileOpen(true)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: T.muted, padding: 6, minHeight: 44, display: 'flex', alignItems: 'center' }}>
          <Menu size={18} />
        </button>
        <Link to="/" style={{ textDecoration: 'none' }}>
          <Logo variant="full" theme="light" size={24} />
        </Link>
        <div style={{ position: 'relative' }}>
          <div style={{
            width: 30, height: 30, borderRadius: '50%',
            background: T.rule, border: `1px solid ${T.ruleDark}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <span style={{ fontFamily: T.display, fontSize: 11, fontWeight: 700, color: T.ink2 }}>{initials}</span>
          </div>
          {pendingCount > 0 && (
            <span style={{
              position: 'absolute', top: -3, right: -3,
              width: 9, height: 9, borderRadius: '50%',
              background: T.red,
              border: `1.5px solid ${T.surface}`,
            }} />
          )}
        </div>
      </header>

      {/* ===== Mobile bottom nav =====
          On garde 4 items (Accueil, Matchs, Effectif, Statistiques) — c'est le
          maximum lisible en bas d'écran. La Bibliothèque reste accessible via
          le drawer (menu burger). Ajouter un 5e item ici dégrade la lisibilité
          sur petits écrans. */}
      <nav data-print="hide" style={{
        position: 'fixed', bottom: 0, left: 0, right: 0,
        background: T.surface, borderTop: `1px solid ${T.sidebarBorder}`,
        display: isMobile ? 'flex' : 'none',
        zIndex: 80,
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}>
        {navigation.filter(i => ['Accueil','Matchs','Effectif','Statistiques'].includes(i.name)).map(item => {
          const active = isActive(item.href)
          const hasBadge = item.badge > 0
          return (
            <Link key={item.name} to={item.href} style={{
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              padding: '10px 4px 8px', gap: 4,
              color: active ? T.ink : T.muted,
              textDecoration: 'none',
              borderTop: `2px solid ${active ? T.ink : 'transparent'}`,
              background: 'transparent',
              transition: 'all .12s',
              minHeight: 44,
            }}>
              <div style={{ position: 'relative' }}>
                <item.icon size={16} strokeWidth={active ? 2.5 : 1.5} />
                {hasBadge && (
                  <span style={{
                    position: 'absolute', top: -3, right: -4,
                    width: 7, height: 7, borderRadius: '50%',
                    background: T.red,
                    border: `1.5px solid ${T.surface}`,
                  }} />
                )}
              </div>
              <span style={{ fontFamily: T.mono, fontSize: 8, fontWeight: active ? 600 : 400, letterSpacing: '.04em' }}>
                {item.name}
              </span>
            </Link>
          )
        })}
      </nav>

      {/* Main */}
      <main data-print="main" style={{
        flex: 1,
        marginLeft: isMobile ? 0 : W,
        minHeight: '100vh',
        background: T.bg,
        padding: isMobile ? '64px 16px 88px' : '32px 36px 48px',
        overflowX: 'hidden',
        maxWidth: '100vw',
      }}>
        <TrialBanner />

        {/* Bandeau saison archivée */}
        {isPastSeason && !isOrphan && (
          <div style={{
            background: 'rgba(99,102,241,0.06)',
            border: '1px solid rgba(99,102,241,0.2)',
            borderLeft: '3px solid #6366f1',
            padding: '12px 18px',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
          }}>
            <Lock size={13} color="#6366f1" style={{ flexShrink: 0 }} />
            <div>
              <p style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: '#6366f1', margin: '0 0 2px' }}>
                Saison archivée — lecture seule
              </p>
              <p style={{ fontSize: 12, color: T.ink, lineHeight: 1.5, margin: 0 }}>
                Tu consultes la saison {selectedSeason}. Les modifications sont désactivées.
              </p>
            </div>
          </div>
        )}

        {/* Bandeau orphelin : affiché quand le coach n'est plus rattaché au club pour la saison sélectionnée */}
        {isOrphan && (
          <div style={{
            background: 'rgba(245,158,11,0.06)',
            border: `1px solid rgba(245,158,11,0.25)`,
            borderLeft: `3px solid #f59e0b`,
            padding: '14px 18px',
            marginBottom: 20,
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            flexWrap: 'wrap',
          }}>
            <div style={{ flex: 1, minWidth: 200 }}>
              <p style={{ fontFamily: T.mono, fontSize: 9, letterSpacing: '.14em', textTransform: 'uppercase', color: '#b45309', marginBottom: 4 }}>
                Lecture seule · Saison {selectedSeason}
              </p>
              <p style={{ fontSize: 13, color: T.ink, lineHeight: 1.5, margin: 0 }}>
                {orphanClubName
                  ? <>Tu n'es plus rattaché à <strong>{orphanClubName}</strong> pour cette saison. Tu peux consulter ton historique mais pas créer de nouveaux contenus.</>
                  : <>Tu n'es plus rattaché à un club pour cette saison. Tu peux consulter ton historique mais pas créer de nouveaux contenus.</>
                }
              </p>
            </div>
            <Link to="/tarifs" style={{
              padding: '10px 18px',
              background: '#f59e0b',
              color: '#1a1916',
              fontFamily: T.mono, fontSize: 10, letterSpacing: '.1em', textTransform: 'uppercase', fontWeight: 700,
              textDecoration: 'none',
              whiteSpace: 'nowrap',
              transition: 'background .12s',
            }}
              onMouseEnter={e => e.currentTarget.style.background = '#d97706'}
              onMouseLeave={e => e.currentTarget.style.background = '#f59e0b'}
            >
              Reprendre une équipe →
            </Link>
          </div>
        )}

        {children}
      </main>
    </div>
  )
}

export default DashboardLayout
